import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, Trash } from '@phosphor-icons/react';
import { useCart } from '../context/CartContext';
import { useAdmin } from '../context/AdminContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [addingToCart, setAddingToCart] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { addToCart } = useCart();
  const { isAuthenticated, token } = useAdmin();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
        if (response.data.sizes && response.data.sizes.length > 0) {
          setSelectedSize(response.data.sizes[0]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }

    setAddingToCart(true);
    try {
      await addToCart(product.id, selectedSize, 1);
      toast.success('Added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      navigate('/shop/men');
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen px-6 md:px-12 lg:px-24 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="aspect-[3/4] bg-muted animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-muted animate-pulse w-3/4" />
            <div className="h-4 bg-muted animate-pulse w-1/4" />
            <div className="h-6 bg-muted animate-pulse w-1/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground text-lg mb-4">PRODUCT NOT FOUND</p>
          <Link to="/shop/men">
            <Button className="bg-white text-black hover:bg-primary hover:text-white">
              BACK TO SHOP
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="px-6 md:px-12 lg:px-24 py-4 border-b border-border">
        <Link 
          to={`/shop/${product.category}`}
          className="text-xs uppercase tracking-wide text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
          data-testid="back-to-shop"
        >
          <ArrowLeft size={14} />
          BACK TO {product.category.toUpperCase()}
        </Link>
      </div>

      <div className="px-6 md:px-12 lg:px-24 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Product Image */}
          <div className="aspect-square bg-black border border-border overflow-hidden flex items-center justify-center" data-testid="product-image">
            <img 
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          </div>

          {/* Product Info */}
          <div className="lg:py-8">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">
              {product.subcategory}
            </p>
            <h1 
              className="text-4xl sm:text-5xl uppercase tracking-tight font-bold mb-4"
              style={{ fontFamily: "'Oswald', sans-serif" }}
              data-testid="product-name"
            >
              {product.name}
            </h1>
            <p 
              className="text-2xl font-bold mb-6"
              data-testid="product-price"
            >
              {formatPrice(product.price)}
            </p>

            <p className="text-muted-foreground leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Size Selector */}
            <div className="mb-8">
              <label className="block text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">
                SELECT SIZE
              </label>
              <Select value={selectedSize} onValueChange={setSelectedSize}>
                <SelectTrigger 
                  className="w-full md:w-48 border-border bg-transparent"
                  data-testid="size-selector"
                >
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent className="bg-black border-border">
                  {product.sizes.map((size) => (
                    <SelectItem 
                      key={size} 
                      value={size}
                      className="hover:bg-muted"
                      data-testid={`size-option-${size}`}
                    >
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Add to Cart */}
            <div className="flex gap-4">
              <Button
                onClick={handleAddToCart}
                disabled={addingToCart || !selectedSize}
                className="flex-1 md:flex-none bg-white text-black hover:bg-primary hover:text-white transition-colors h-14 px-12 text-base font-bold uppercase tracking-wide disabled:opacity-50"
                data-testid="add-to-cart-btn"
              >
                {addingToCart ? 'ADDING...' : 'ADD TO CART'}
              </Button>

              {isAuthenticated && (
                <Button
                  onClick={() => setShowDeleteDialog(true)}
                  variant="outline"
                  className="h-14 px-6 border-destructive text-destructive hover:bg-destructive hover:text-white transition-colors"
                  data-testid="delete-product-btn"
                >
                  <Trash size={20} />
                </Button>
              )}
            </div>

            {/* Product Details */}
            <div className="mt-12 pt-8 border-t border-border">
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
                DETAILS
              </h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <Check size={16} className="text-primary" />
                  PREMIUM QUALITY MATERIALS
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check size={16} className="text-primary" />
                  OVERSIZED FIT
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check size={16} className="text-primary" />
                  MACHINE WASHABLE
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check size={16} className="text-primary" />
                  FREE RETURNS WITHIN 30 DAYS
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-black border-border">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "'Oswald', sans-serif" }}>
              DELETE PRODUCT
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{product?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border hover:bg-muted">
              CANCEL
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'DELETING...' : 'DELETE'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
